import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calculator, ChevronUp, FileText } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AutoCalculateOptions } from './utils/types';
import { useUnifiedCalculator } from '@/hooks/useUnifiedCalculator';
import { useLocationData } from './tabs/useLocationData';
import CalculatorTabsContent from './tabs/TabsContent';
import { EnhancedResultsDisplay } from './EnhancedResultsDisplay';

interface MobileCalculatorInterfaceProps {
  hasBottomBanner?: boolean;
}

export const MobileCalculatorContent = ({ calculator, autoOptions, hasBottomBanner = false }: MobileCalculatorInterfaceProps & { autoOptions: [AutoCalculateOptions, React.Dispatch<React.SetStateAction<AutoCalculateOptions>>]; calculator: ReturnType<typeof useUnifiedCalculator> }) => {
  const { mortgageData, handlers, setMortgageData } = calculator;
  const { states, cities } = useLocationData(mortgageData);
  const [showResults, setShowResults] = useState(false);
  const resultsRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (showResults) resultsRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }, [showResults]);
  const [autoCalculate, setAutoCalculate] = autoOptions;

  return (
    <div className={`min-h-dvh bg-background ${hasBottomBanner ? 'pb-40' : 'pb-24'}`}>
      {/* Mobile Header */}
      <div className="bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b">
        <div className="flex items-center gap-2 p-4">
          <Calculator className="w-5 h-5 text-primary" />
          <h1 className="text-lg font-semibold">Payment Estimator</h1>
        </div>
      </div>

      {/* Main Content */}
      <div className="p-4 space-y-4">
        {/* Calculator Inputs - Accordion Style */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Calculator Inputs
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <CalculatorTabsContent
              mortgageData={mortgageData}
              setMortgageData={setMortgageData}
              autoCalculate={autoCalculate}
              setAutoCalculate={setAutoCalculate}
              states={states}
              cities={cities}
              calculatorHandlers={handlers}
            />
          </CardContent>
        </Card>

        {/* Results Section */}
        <AnimatePresence>
          {showResults && (
            <motion.div
              ref={resultsRef}
              className="scroll-mt-40"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.3 }}
            >
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">Results</CardTitle>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowResults(false)}
                    >
                      <ChevronUp className="w-4 h-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <EnhancedResultsDisplay mortgageData={mortgageData} onHomePriceChange={handlers.handleHomePriceChange} />
                </CardContent>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Sticky Bottom Action Button */}
      <div className="calculator-bottom-actions fixed bottom-0 left-0 right-0 p-4 bg-background border-t shadow-lg z-50">
        <Button
          onClick={() => setShowResults(!showResults)}
          className="w-full h-12 text-base font-medium"
          size="lg"
        >
          {showResults ? (
            <>
              <ChevronUp className="w-5 h-5 mr-2" />
              Hide Results
            </>
          ) : (
            <>
              <Calculator className="w-5 h-5 mr-2" />
              View Results
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export const MobileCalculatorInterface = (props: MobileCalculatorInterfaceProps) => {
  const calculator = useUnifiedCalculator();
  const autoOptions = useState<AutoCalculateOptions>({ propertyTax: true, insurance: true, pmi: true, closingCosts: true });
  return <MobileCalculatorContent {...props} calculator={calculator} autoOptions={autoOptions} />;
};
